import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeftRight,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  CircleHelp,
  FileBarChart,
  Info,
  ListChecks,
  Settings,
} from "lucide-react";
import {
  DsBadge,
  DsEmptyState,
  DsIcon,
  DsPageHeader,
} from "../../../design-system";
import { usePermissions } from "../../../rbac/PermissionsContext";
import {
  WORKSPACE_DEFS,
  WORKSPACE_MODULES,
  buildFeatureHref,
  getFeaturesForWorkspace,
  getWorkspaceDef,
} from "../../../nav/workspaceRegistry";
import {
  employeeService,
  payrollService,
  salaryStructureService,
  branchService,
} from "../../../services/api";
import WorkspaceIoModal from "./WorkspaceIoModal";
import { WORKSPACE_IO_DEFS } from "./workspaceIoActions";
import ChecksImportModal from "./ChecksImportModal";
import SalaryRevisionImportModal from "../payroll/SalaryRevisionImportModal";
import StructureComponentImportModal from "../payroll/StructureComponentImportModal";
import MastersImportModal from "../masters/MastersImportModal";
import { templatesToStructures } from "../../../utils/payrollSalaryBreakdownEnrichment";
import { isCompanyModuleEnabled } from "../../../utils/companyModules";

const WORKSPACE_ICONS = {
  reports: FileBarChart,
  import_export: ArrowLeftRight,
  checklist: ListChecks,
  settings: Settings,
  help: CircleHelp,
};

/** Flat module cards + hyperlink lists (not accordion). */
const CARD_LAYOUT_WORKSPACES = new Set([
  "reports",
  "import_export",
  "checklist",
  "settings",
]);

const IO_MODAL_FEATURE_IDS = new Set(Object.keys(WORKSPACE_IO_DEFS));

function featureAllowed(feature, { isAdmin, hasScreenAccess }) {
  if (isAdmin) return true;
  const action = feature.rbacAction || "view";
  return hasScreenAccess(feature.rbacModule, feature.rbacScreen, action);
}

function ModuleCardGrid({ modules, onFeatureClick }) {
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {modules.map((mod) => {
        const items = [...mod.cards, ...mod.links];
        return (
          <section
            key={mod.key}
            className="flex flex-col rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-4 shadow-[var(--ds-shadow-card)]"
          >
            <div className="mb-3 flex items-center justify-between gap-2 border-b border-[hsl(var(--ds-border))] pb-3">
              <h2 className="ds-h4 text-[hsl(var(--ds-fg))]">{mod.label}</h2>
              <DsBadge tone="neutral">{items.length}</DsBadge>
            </div>
            <ul className="flex flex-col gap-1.5">
              {items.map((feature) => (
                <li key={feature.id}>
                  <button
                    type="button"
                    onClick={() => onFeatureClick(feature)}
                    className="group flex w-full items-center justify-between gap-2 rounded-md px-1 py-1.5 text-left text-sm font-medium text-[hsl(var(--ds-fg))] underline-offset-4 hover:text-[hsl(var(--ds-primary))] hover:underline"
                  >
                    <span className="truncate">{feature.label}</span>
                    <ArrowRight
                      size={14}
                      className="shrink-0 opacity-0 transition group-hover:opacity-70"
                    />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

function AccordionModules({
  modules,
  openModule,
  onToggle,
  onFeatureClick,
}) {
  return (
    <div className="space-y-3">
      {modules.map((mod) => {
        const open = openModule === mod.key;
        return (
          <section
            key={mod.key}
            className="overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]"
          >
            <button
              type="button"
              onClick={() => onToggle(mod.key)}
              className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition hover:bg-[hsl(var(--ds-muted))]/50"
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="ds-h4 text-[hsl(var(--ds-fg))]">{mod.label}</h2>
                  <DsBadge tone="neutral">{mod.cards.length + mod.links.length}</DsBadge>
                </div>
                <p className="ds-caption mt-0.5 text-[hsl(var(--ds-muted-fg))]">
                  {open ? "Hide section" : "Show cards and quick links"}
                </p>
              </div>
              {open ? (
                <ChevronUp size={18} className="shrink-0 text-[hsl(var(--ds-muted-fg))]" />
              ) : (
                <ChevronDown size={18} className="shrink-0 text-[hsl(var(--ds-muted-fg))]" />
              )}
            </button>

            {open && (
              <div className="space-y-4 border-t border-[hsl(var(--ds-border))] px-4 py-4">
                {mod.cards.length > 0 && (
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {mod.cards.map((feature) => (
                      <button
                        key={feature.id}
                        type="button"
                        onClick={() => onFeatureClick(feature)}
                        className="group flex flex-col rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-bg))] p-4 text-left transition hover:border-[hsl(var(--ds-primary))]/40 hover:bg-[hsl(var(--ds-surface))] hover:shadow-[var(--ds-shadow-card)]"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <span className="ds-body font-semibold text-[hsl(var(--ds-fg))]">
                            {feature.label}
                          </span>
                          <ArrowRight
                            size={16}
                            className="mt-0.5 shrink-0 text-[hsl(var(--ds-muted-fg))] opacity-0 transition group-hover:opacity-100"
                          />
                        </div>
                        {feature.description ? (
                          <p className="ds-caption mt-2 text-[hsl(var(--ds-muted-fg))]">
                            {feature.description}
                          </p>
                        ) : null}
                      </button>
                    ))}
                  </div>
                )}

                {mod.links.length > 0 && (
                  <div>
                    <p className="ds-caption mb-2 font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                      Quick links
                    </p>
                    <ul className="divide-y divide-[hsl(var(--ds-border))] rounded-lg border border-[hsl(var(--ds-border))]">
                      {mod.links.map((feature) => (
                        <li key={feature.id}>
                          <button
                            type="button"
                            onClick={() => onFeatureClick(feature)}
                            className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left text-sm font-medium text-[hsl(var(--ds-fg))] transition hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-primary))] hover:underline"
                          >
                            <span className="truncate">{feature.label}</span>
                            <ArrowRight size={14} className="shrink-0 opacity-60" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

export default function WorkspacePage({ workspaceKey: workspaceKeyProp }) {
  const { workspaceKey: workspaceKeyParam } = useParams();
  const location = useLocation();
  const workspaceKeyFromPath =
    (location.pathname.match(/\/admin\/workspace\/([^/?#]+)/) || [])[1] || "";
  const workspaceKey =
    workspaceKeyProp || workspaceKeyParam || workspaceKeyFromPath || "reports";
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAdmin, hasScreenAccess } = usePermissions();

  const def = getWorkspaceDef(workspaceKey);
  const Icon = WORKSPACE_ICONS[workspaceKey] || FileBarChart;
  const useCardLayout = CARD_LAYOUT_WORKSPACES.has(workspaceKey);

  const [ioFeatureId, setIoFeatureId] = useState(null);
  const [checksImportOpen, setChecksImportOpen] = useState(false);
  const [salaryRevisionOpen, setSalaryRevisionOpen] = useState(false);
  const [structureComponentsOpen, setStructureComponentsOpen] = useState(false);
  const [mastersImportOpen, setMastersImportOpen] = useState(false);
  const [salaryEmployees, setSalaryEmployees] = useState([]);
  const [allowMidMonth, setAllowMidMonth] = useState(false);
  const [structureEmployees, setStructureEmployees] = useState([]);
  const [salaryStructures, setSalaryStructures] = useState([]);
  const [statutoryConfigJson, setStatutoryConfigJson] = useState(null);
  const [branchesList, setBranchesList] = useState([]);

  const features = useMemo(() => {
    return getFeaturesForWorkspace(workspaceKey).filter(
      (f) =>
        isCompanyModuleEnabled(f.moduleKey) &&
        featureAllowed(f, { isAdmin, hasScreenAccess })
    );
  }, [workspaceKey, isAdmin, hasScreenAccess]);

  const modulesWithFeatures = useMemo(() => {
    return WORKSPACE_MODULES.map((mod) => ({
      ...mod,
      cards: features.filter((f) => f.moduleKey === mod.key && f.kind === "card"),
      links: features.filter((f) => f.moduleKey === mod.key && f.kind === "link"),
    })).filter((mod) => mod.cards.length > 0 || mod.links.length > 0);
  }, [features]);

  const moduleFromUrl = searchParams.get("module") || "";
  const [openModule, setOpenModule] = useState(() => {
    if (moduleFromUrl) return moduleFromUrl;
    return modulesWithFeatures[0]?.key || "";
  });

  useEffect(() => {
    if (useCardLayout || !modulesWithFeatures.length) {
      if (!modulesWithFeatures.length) setOpenModule("");
      return;
    }
    if (moduleFromUrl && modulesWithFeatures.some((m) => m.key === moduleFromUrl)) {
      setOpenModule(moduleFromUrl);
      return;
    }
    if (!modulesWithFeatures.some((m) => m.key === openModule)) {
      setOpenModule(modulesWithFeatures[0].key);
    }
  }, [modulesWithFeatures, moduleFromUrl, openModule, useCardLayout]);

  useEffect(() => {
    if (!salaryRevisionOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const [empRes, tplRes, statRes, branchesRes, settingsRes] = await Promise.all([
          employeeService.getEmployees({
            page: 1,
            limit: 5000,
            fields: "id,empCode,employeeCode,name,ctc,ctcAmount",
          }),
          salaryStructureService.listTemplates(),
          salaryStructureService.getStatutoryConfig(),
          branchService.getBranches(),
          payrollService.getPayrollSettings(),
        ]);
        if (cancelled) return;
        const list = empRes?.data || empRes?.employees || empRes || [];
        setSalaryEmployees(Array.isArray(list) ? list : []);
        setSalaryStructures(templatesToStructures(tplRes?.data) || []);
        setStatutoryConfigJson(statRes?.data?.configJson || null);
        const branches =
          branchesRes?.data || branchesRes?.branches || branchesRes || [];
        setBranchesList(Array.isArray(branches) ? branches : []);
        const settings = settingsRes?.data || settingsRes || {};
        setAllowMidMonth(!!settings.allowMidMonthIncrements);
      } catch {
        if (!cancelled) {
          setSalaryEmployees([]);
          setSalaryStructures([]);
          setStatutoryConfigJson(null);
          setBranchesList([]);
          setAllowMidMonth(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [salaryRevisionOpen]);

  useEffect(() => {
    if (!structureComponentsOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const [empRes, tplRes, statRes, branchesRes] = await Promise.all([
          employeeService.getEmployees({ page: 1, limit: 5000 }),
          salaryStructureService.listTemplates(),
          salaryStructureService.getStatutoryConfig(),
          branchService.getBranches(),
        ]);
        if (cancelled) return;
        const list = empRes?.data || empRes?.employees || empRes || [];
        setStructureEmployees(Array.isArray(list) ? list : []);
        setSalaryStructures(templatesToStructures(tplRes?.data) || []);
        setStatutoryConfigJson(statRes?.data?.configJson || null);
        const branches =
          branchesRes?.data || branchesRes?.branches || branchesRes || [];
        setBranchesList(Array.isArray(branches) ? branches : []);
      } catch {
        if (!cancelled) {
          setStructureEmployees([]);
          setSalaryStructures([]);
          setStatutoryConfigJson(null);
          setBranchesList([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [structureComponentsOpen]);

  const toggleModule = (key) => {
    const next = openModule === key ? "" : key;
    setOpenModule(next);
    const nextParams = new URLSearchParams(searchParams);
    if (next) nextParams.set("module", next);
    else nextParams.delete("module");
    setSearchParams(nextParams, { replace: true });
  };

  const openFeature = (feature) => {
    if (workspaceKey === "import_export") {
      if (feature.id === "pay-import-revision") {
        setSalaryRevisionOpen(true);
        return;
      }
      if (feature.id === "pay-import-structure-components") {
        setStructureComponentsOpen(true);
        return;
      }
      if (feature.id === "cfg-import-masters") {
        setMastersImportOpen(true);
        return;
      }
      if (feature.id === "att-import-checks") {
        setChecksImportOpen(true);
        return;
      }
      if (IO_MODAL_FEATURE_IDS.has(feature.id)) {
        setIoFeatureId(feature.id);
        return;
      }
    }
    navigate(buildFeatureHref(feature));
  };

  if (workspaceKey === "reports" && !isCompanyModuleEnabled("reports")) {
    return <Navigate to="/admin/home" replace />;
  }

  if (!def) {
    return (
      <div className="ds-page">
        <div className="ds-page-shell">
          <div className="ds-page-content">
            <DsEmptyState
              title="Workspace not found"
              description="This workspace hub is not configured."
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ds-page">
      <div className="ds-page-shell">
        <div className="ds-page-content space-y-6">
          <DsPageHeader
            title={def.label}
            infoTooltip={def.description}
          />

          {modulesWithFeatures.length === 0 ? (
            <DsEmptyState
              title={`No ${def.label.toLowerCase()} available`}
              description="Your role does not include any features in this workspace. Ask an administrator to grant access."
            />
          ) : useCardLayout ? (
            <ModuleCardGrid modules={modulesWithFeatures} onFeatureClick={openFeature} />
          ) : (
            <AccordionModules
              modules={modulesWithFeatures}
              openModule={openModule}
              onToggle={toggleModule}
              onFeatureClick={openFeature}
            />
          )}
        </div>
      </div>

      <WorkspaceIoModal
        featureId={ioFeatureId}
        isOpen={!!ioFeatureId}
        onClose={() => setIoFeatureId(null)}
      />

      <ChecksImportModal
        isOpen={checksImportOpen}
        onClose={() => setChecksImportOpen(false)}
      />

      <SalaryRevisionImportModal
        isOpen={salaryRevisionOpen}
        onClose={() => setSalaryRevisionOpen(false)}
        employees={salaryEmployees}
        structures={salaryStructures}
        statutoryConfigJson={statutoryConfigJson}
        branchesList={branchesList}
        allowMidMonthIncrements={allowMidMonth}
        onImported={() => setSalaryRevisionOpen(false)}
      />

      <StructureComponentImportModal
        isOpen={structureComponentsOpen}
        onClose={() => setStructureComponentsOpen(false)}
        employees={structureEmployees}
        structures={salaryStructures}
        statutoryConfigJson={statutoryConfigJson}
        branchesList={branchesList}
        onImported={() => setStructureComponentsOpen(false)}
      />

      <MastersImportModal
        isOpen={mastersImportOpen}
        onClose={() => setMastersImportOpen(false)}
        onImported={() => setMastersImportOpen(false)}
      />
    </div>
  );
}
